import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ApiNavShell,
  ApiNavReorderRequest,
  ApiPatchNavGroupRequest,
  ApiPatchNavItemRequest,
} from "@tm-food/api-contract";
import {
  fetchNavCatalogDraft,
  fetchRuntimeNav,
  patchNavDraftGroup,
  patchNavDraftItem,
  deleteNavDraftItem,
  publishNav,
  reorderNavDraft,
  rollbackNav,
} from "@/modules/sys/api/navApi";
import { useAuth } from "@/shared/auth/AuthContext";

/** FRS-SYS-06 — runtime nav for a shell (W2). */
export function useNavQuery(shell: ApiNavShell, enabled = true) {
  const { http, user } = useAuth();
  return useQuery({
    queryKey: ["sys", "nav", shell, user?.id ?? "anon"],
    queryFn: () => fetchRuntimeNav(http, shell),
    enabled: enabled && Boolean(user),
    staleTime: 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useNavCatalogDraftQuery(shell: ApiNavShell = "ops") {
  const { http, user } = useAuth();
  return useQuery({
    queryKey: ["sys", "nav", "catalog", shell],
    queryFn: () => fetchNavCatalogDraft(http, shell),
    enabled: Boolean(user),
    staleTime: 15_000,
  });
}

function invalidateNav(qc: ReturnType<typeof useQueryClient>, shell: ApiNavShell) {
  void qc.invalidateQueries({ queryKey: ["sys", "nav", "catalog", shell] });
  void qc.invalidateQueries({ queryKey: ["sys", "nav", shell] });
}

export function usePatchNavGroupMutation(shell: ApiNavShell = "ops") {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      body: ApiPatchNavGroupRequest;
      rowVersion: string;
    }) => patchNavDraftGroup(http, args.id, args.body, args.rowVersion),
    onSuccess: () => invalidateNav(qc, shell),
  });
}

export function usePatchNavItemMutation(shell: ApiNavShell = "ops") {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      body: ApiPatchNavItemRequest;
      rowVersion: string;
    }) => patchNavDraftItem(http, args.id, args.body, args.rowVersion),
    onSuccess: () => invalidateNav(qc, shell),
  });
}

export function useDeleteNavItemMutation(shell: ApiNavShell = "ops") {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteNavDraftItem(http, id),
    onSuccess: () => invalidateNav(qc, shell),
  });
}

export function useReorderNavMutation(shell: ApiNavShell = "ops") {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApiNavReorderRequest) => reorderNavDraft(http, body),
    onSuccess: () => invalidateNav(qc, shell),
  });
}

export function usePublishNavMutation(shell: ApiNavShell = "ops") {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (comment?: string) => publishNav(http, shell, comment),
    onSuccess: () => invalidateNav(qc, shell),
  });
}

export function useRollbackNavMutation(shell: ApiNavShell = "ops") {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { targetVersion: number; comment?: string }) =>
      rollbackNav(http, shell, args.targetVersion, args.comment),
    onSuccess: () => invalidateNav(qc, shell),
  });
}
